import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { fetchInstructorDocumentsStatus, submitInstructorDocuments } from '../../util/api';

import { submissionBody } from './InstructorDocumentsPage.documents';

// The app's own API answers errors as plain JSON; keep only what the page needs to explain them.
const plainError = e => ({ status: e?.status || null, code: e?.code || null });

// ================ Thunks ================ //

export const fetchStatusThunk = createAsyncThunk(
  'InstructorDocumentsPage/fetchStatus',
  (_arg, { rejectWithValue }) =>
    fetchInstructorDocumentsStatus().catch(e => rejectWithValue(plainError(e)))
);

export const submitDocumentsThunk = createAsyncThunk(
  'InstructorDocumentsPage/submitDocuments',
  (values, { rejectWithValue }) =>
    submitInstructorDocuments(submissionBody(values)).catch(e => rejectWithValue(plainError(e)))
);

// ================ Slice ================ //

const instructorDocumentsPageSlice = createSlice({
  name: 'InstructorDocumentsPage',
  initialState: {
    statusInProgress: false,
    statusError: null,
    status: null, // { submitted, submittedAt }
    submitInProgress: false,
    submitError: null,
    submitSucceeded: false,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchStatusThunk.pending, state => {
        state.statusInProgress = true;
        state.statusError = null;
      })
      .addCase(fetchStatusThunk.fulfilled, (state, action) => {
        state.statusInProgress = false;
        state.status = action.payload;
      })
      .addCase(fetchStatusThunk.rejected, (state, action) => {
        state.statusInProgress = false;
        state.statusError = action.payload || plainError(action.error);
      })
      .addCase(submitDocumentsThunk.pending, state => {
        state.submitInProgress = true;
        state.submitError = null;
      })
      .addCase(submitDocumentsThunk.fulfilled, state => {
        state.submitInProgress = false;
        state.submitSucceeded = true;
      })
      .addCase(submitDocumentsThunk.rejected, (state, action) => {
        state.submitInProgress = false;
        state.submitError = action.payload || plainError(action.error);
      });
  },
});

export default instructorDocumentsPageSlice.reducer;

// ================ Load data ================ //

/**
 * Asks whether the instructor has already sent their documents. Browser only: the request goes
 * to this app's own server with the session cookie, so the server render shows the loading state.
 */
export const loadData = () => dispatch => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  return dispatch(fetchStatusThunk());
};
